import React, { createContext, useContext } from 'react';
import DownloadProgress from '../../components/DownloadProgress/DownloadProgress';
import { 
  DownloadProgressProviderProps, 
  DownloadProgressContextType 
} from './types';
import { useDownloadProgressProvider } from './useDownloadProgressProvider';
import styles from './DownloadProgressProvider.module.css';

// 空の処理を行うデフォルト関数
const noopDownloadProgress: DownloadProgressContextType = {
  startDownloadProgress: () => {},
  updateDownloadProgress: () => {},
  completeDownloadProgress: () => {},
  closeDownloadProgress: () => {}
};

// ダウンロード進捗コンテキストの作成
const DownloadProgressContext = createContext<DownloadProgressContextType>(noopDownloadProgress);

/**
 * ダウンロード進捗を管理するためのフック
 * どのコンポーネントからでもダウンロード進捗を管理できるようにする
 */
export const useDownloadProgress = (): DownloadProgressContextType => {
  const context = useContext(DownloadProgressContext);
  return context;
};

/**
 * アプリケーション全体でダウンロード進捗を管理・表示するためのプロバイダーコンポーネント
 * 子コンポーネントからuseDownloadProgressフックを使用してダウンロード進捗を管理可能にする
 */
export const DownloadProgressProvider: React.FC<DownloadProgressProviderProps> = ({ children }) => {
  const { 
    startDownloadProgress, 
    updateDownloadProgress,
    completeDownloadProgress,
    closeDownloadProgress,
    downloadProgresses 
  } = useDownloadProgressProvider();

  return (
    <DownloadProgressContext.Provider 
      value={{
        startDownloadProgress,
        updateDownloadProgress,
        completeDownloadProgress,
        closeDownloadProgress
      }}
    >
      {children}
      {/* 複数のダウンロード進捗をレンダリング */}
      <div 
        className={styles['download-progress-provider-container']} 
        data-testid="download-progress-provider-container"
      >
        {downloadProgresses.map((progress, index) => (
          <DownloadProgress
            key={progress.id}
            id={progress.id}
            fileName={progress.fileName}
            progress={progress.progress}
            loaded={progress.loaded}
            total={progress.total}
            open={progress.open}
            onClose={() => closeDownloadProgress(progress.id)}
            index={index}
          />
        ))}
      </div>
    </DownloadProgressContext.Provider>
  );
};
